/**
 * Artifact viewer (web) for places the docked canvas panel cannot reach:
 * cards inside a dialog (the Tasks run panel and run history) and list rows.
 * Images open the full-viewport lightbox; documents, slides and pages open
 * the canvas panel covering the viewport, above the dialog. Everywhere else the
 * cards open the docked panel through the panel store instead.
 * `ArtifactViewer.tsx` is the native twin.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useArtifactDetail } from '../../hooks/useArtifact';
import { ArtifactPanel } from './ArtifactPanel';
import { ImageLightbox } from './ImageLightbox';
import type { ArtifactViewerProps } from './artifact-model';

/** Above WebDialog (1000) and the run panel it opens from. */
const OVERLAY_Z_INDEX = 1100;

export function ArtifactViewer({ artifactId, onClose, kindHint }: ArtifactViewerProps) {
  const detail = useArtifactDetail(artifactId);
  const kind = kindHint ?? detail.data?.kind ?? null;
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document !== 'undefined') setTarget(document.body);
  }, []);

  if (kind === 'image') {
    return (
      <ImageLightbox
        artifactId={artifactId}
        title={detail.data?.title ?? ''}
        onClose={onClose}
      />
    );
  }

  if (!target) return null;
  return createPortal(
    <div className="tabbio-artifact-overlay" style={{ zIndex: OVERLAY_Z_INDEX }}>
      <ArtifactPanel artifactId={artifactId} layout="fullscreen" onClose={onClose} />
    </div>,
    target,
  );
}
